import { useEffect, useRef } from "react";

/**
 * Returns a ref to attach to a search-style input. Pressing "/" anywhere
 * outside an editable element focuses it. The sidebar's search chip also
 * dispatches a `focus-page-search` custom event which we listen for here,
 * so clicking it works on any page that's wired this hook into its input.
 */
export const FOCUS_SEARCH_EVENT = "focus-page-search";

export function focusPageSearch() {
  document.dispatchEvent(new CustomEvent(FOCUS_SEARCH_EVENT));
}

export function useFocusSearchShortcut() {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const focusInput = () => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.select?.();
    };

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key !== "/") return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      const editable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable;
      if (editable) return;
      event.preventDefault();
      focusInput();
    };

    document.addEventListener("keydown", onKeydown);
    document.addEventListener(FOCUS_SEARCH_EVENT, focusInput);
    return () => {
      document.removeEventListener("keydown", onKeydown);
      document.removeEventListener(FOCUS_SEARCH_EVENT, focusInput);
    };
  }, []);

  return ref;
}
